import React, { useState, useEffect, useMemo } from 'react';
import { Input } from './ui/input';

/**
 * AntiBot Component - Protects forms from bots
 * Uses honeypot, time check, and math captcha
 */
export const AntiBot = ({ onValidChange, language = 'it' }) => {
  // Honeypot field (hidden, bots will fill it)
  const [honeypot, setHoneypot] = useState('');
  
  // Form load time
  const [loadTime] = useState(Date.now());
  
  // Math captcha
  const [userAnswer, setUserAnswer] = useState('');
  
  // Generate random math question
  const mathQuestion = useMemo(() => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    const operators = ['+', '-'];
    const operator = operators[Math.floor(Math.random() * operators.length)];
    
    let answer;
    let a = num1, b = num2;
    
    if (operator === '+') {
      answer = num1 + num2;
    } else {
      // Ensure positive result for subtraction
      if (num1 < num2) {
        a = num2;
        b = num1;
      }
      answer = a - b;
    }
    
    return { num1: a, num2: b, operator, answer };
  }, []);

  // Labels in different languages
  const labels = {
    it: {
      question: 'Verifica di sicurezza',
      placeholder: 'Risposta',
      hint: 'Risolvi per dimostrare che non sei un robot'
    },
    de: {
      question: 'Sicherheitsüberprüfung',
      placeholder: 'Antwort',
      hint: 'Lösen Sie, um zu beweisen, dass Sie kein Roboter sind'
    },
    fr: {
      question: 'Vérification de sécurité',
      placeholder: 'Réponse',
      hint: 'Résolvez pour prouver que vous n\'êtes pas un robot'
    },
    en: {
      question: 'Security check',
      placeholder: 'Answer',
      hint: 'Solve to prove you\'re not a robot'
    }
  };

  const t = labels[language] || labels.en;

  // Validate anti-bot checks (time check is server-side only)
  useEffect(() => {
    const isHoneypotEmpty = honeypot === '';
    const isMathCorrect = parseInt(userAnswer) === mathQuestion.answer;
    
    const isValid = isHoneypotEmpty && isMathCorrect;
    
    // Pass validation status and captcha data to parent
    onValidChange(isValid, {
      answer: parseInt(userAnswer) || 0,
      expected: mathQuestion.answer
    });
  }, [honeypot, userAnswer, mathQuestion.answer, onValidChange]);

  return (
    <div className="space-y-3">
      {/* Honeypot field - hidden from users, bots will fill it */}
      <div style={{ position: 'absolute', left: '-9999px', opacity: 0 }} aria-hidden="true">
        <label htmlFor="website_url">Website</label>
        <input
          type="text"
          id="website_url"
          name="website_url"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      {/* Math CAPTCHA */}
      <div className="bg-[#F5F5F7] p-4 rounded-lg">
        <label className="block text-sm font-medium text-[#0B1221] mb-2">
          {t.question}: {mathQuestion.num1} {mathQuestion.operator} {mathQuestion.num2} = ?
        </label>
        <Input
          type="number"
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          placeholder={t.placeholder}
          className="bg-white border-[#E5E5EA] w-24"
          data-testid="captcha-input"
        />
        <p className="text-xs text-[#6E6E73] mt-2">{t.hint}</p>
      </div>
    </div>
  );
};

export default AntiBot;
